import { TourId } from '../services/tour.service';

export interface TourStep {
  title: string;
  text: string;
  textSecondary?: string;
  /** Bullets cortos y accionables */
  tips?: string[];
  icon: string;
  /** id del elemento a resaltar; null = tarjeta centrada */
  targetId: string | null;
  /** Abre el menú lateral si el target está ahí */
  ensureSidebarOpen?: boolean;
  ensureFirmarMenuOpen?: boolean;
  ensureAdminMenuOpen?: boolean;
}

export interface TourDefinition {
  id: TourId;
  name: string;
  steps: TourStep[];
}

export const TOUR_DEFINITIONS: Record<TourId, TourDefinition> = {
  sign: {
    id: 'sign',
    name: 'Cómo firmar un documento',
    steps: [
      {
        title: 'Guía para firmar',
        text: 'Te acompañamos paso a paso para firmar con el mecanismo que elegiste en el menú.',
        textSecondary: 'Puedes omitir la guía o volver a abrirla cuando quieras desde el botón “Guía” del encabezado.',
        tips: [
          'El menú lateral define tu Certifirma (Virtual con OTP, Virtual o PKCS10).',
          'El área central es donde cargas, revisas y firmas.'
        ],
        icon: 'fa-compass',
        targetId: null
      },
      {
        title: 'Elige el mecanismo',
        text: 'En “Firmar y verificar” selecciona cómo quieres firmar.',
        tips: [
          'Certifirma Digital Virtual con OTP: te enviaremos un código al correo.',
          'Certifirma Digital Virtual: ingresas usuario y contraseña.',
          'Certifirma Digital PKCS10: cargas tu archivo .p10/.pfx y su clave.'
        ],
        icon: 'fa-shield-halved',
        targetId: 'tour-target-firmar-menu',
        ensureSidebarOpen: true,
        ensureFirmarMenuOpen: true
      },
      {
        title: 'Progreso del flujo',
        text: 'Esta barra te indica en qué etapa vas: Cargar → Revisar → Continuar/Firmar.',
        tips: [
          'Los pasos completados se marcan en verde.',
          'El paso actual se destaca para orientarte.'
        ],
        icon: 'fa-list-check',
        targetId: 'tour-target-sign-progress'
      },
      {
        title: 'Carga tus archivos',
        text: 'Paso 1: elige PDF (recomendado para contratos) u Otro archivo (genera un .p7z).',
        tips: [
          'Haz clic en la tarjeta o arrastra el documento encima.',
          'Hasta 10 archivos. Máximo 150 MB por archivo en todos los mecanismos.',
          'Puedes mezclar varios archivos del mismo tipo en una sola operación.'
        ],
        icon: 'fa-cloud-arrow-up',
        targetId: 'tour-target-sign-upload'
      },
      {
        title: 'Revisa la cola',
        text: 'A la derecha verás los archivos listos para firmar y su estado.',
        tips: [
          'Puedes quitar un archivo si te equivocaste.',
          'Cuando la cola esté lista, el botón principal se habilita.'
        ],
        icon: 'fa-folder-open',
        targetId: 'tour-target-sign-queue'
      },
      {
        title: 'Continúa a firmar',
        text: 'Pulsa el botón naranja del pie para continuar a firmar (si aún no autenticaste) y completar la firma.',
        tips: [
          'Certifirma Virtual / PKCS10: se abrirá un modal para credenciales o certificado.',
          'Certifirma Digital Virtual con OTP: seguirás el flujo de autenticación por correo.',
          'Al terminar podrás ver, descargar o enviar los documentos firmados.'
        ],
        icon: 'fa-pen-nib',
        targetId: 'tour-target-sign-actions'
      },
      {
        title: '¡Listo para firmar!',
        text: 'Ya conoces el recorrido. Carga un documento y completa la autenticación cuando el sistema te lo pida.',
        textSecondary: 'Si necesitas ver esta guía otra vez, usa el botón “Guía” arriba a la derecha.',
        icon: 'fa-circle-check',
        targetId: null
      }
    ]
  },

  verify: {
    id: 'verify',
    name: 'Cómo verificar una firma',
    steps: [
      {
        title: 'Guía para verificar',
        text: 'Aquí compruebas la validez y autenticidad de tu documento firmado digitalmente.',
        tips: [
          'Acepta PDF firmados, archivos .p7z / .p7m o XML XAdES (.xml).',
          'El resultado muestra validez, firmantes y detalles del certificado.'
        ],
        icon: 'fa-certificate',
        targetId: null
      },
      {
        title: 'Entrar a Verificar',
        text: 'Desde el menú “Firmar y verificar”, elige “Verificar firmas”.',
        icon: 'fa-certificate',
        targetId: 'tour-target-verificar',
        ensureSidebarOpen: true,
        ensureFirmarMenuOpen: true
      },
      {
        title: 'Carga el documento firmado',
        text: 'Selecciona o arrastra tu archivo firmado (.pdf, .p7z o .xml).',
        tips: [
          'Tamaño máximo aproximado: 10 MB.',
          'Usa el mismo archivo que recibiste firmado (PDF, CAdES o XAdES).'
        ],
        icon: 'fa-file-circle-check',
        targetId: 'tour-target-verify-upload'
      },
      {
        title: 'Valida el documento',
        text: 'Con el archivo cargado, pulsa el botón para validar el documento.',
        tips: [
          'Verás un resumen de validez y los certificados involucrados.',
          'Puedes abrir más detalle de cada firmante si está disponible.'
        ],
        icon: 'fa-magnifying-glass',
        targetId: 'tour-target-verify-actions'
      },
      {
        title: '¡Listo!',
        text: 'Ya puedes verificar documentos cuando lo necesites. Reabre esta guía desde “Guía” en el encabezado.',
        icon: 'fa-circle-check',
        targetId: null
      }
    ]
  },

  'admin-tv': {
    id: 'admin-tv',
    name: 'Administración · Certifirma Digital Virtual',
    steps: [
      {
        title: 'Gestionar Certifirma Digital Virtual',
        text: 'Desde aquí puedes descargar tu certificado o recuperar la clave de tu Certifirma Virtual si la olvidaste.',
        tips: [
          'Usa las pestañas superiores para cambiar entre Descargar y Recuperar clave Virtual.',
          'La recuperación pide documento, verificación y una nueva contraseña.'
        ],
        icon: 'fa-key',
        targetId: null
      },
      {
        title: 'Dónde estás',
        text: 'En Administración → Certifirma Digital Virtual.',
        icon: 'fa-cloud',
        targetId: 'tour-target-admin-tv',
        ensureSidebarOpen: true,
        ensureAdminMenuOpen: true
      },
      {
        title: 'Elige la acción',
        text: 'Las pestañas te permiten ir a descargar el certificado o a Recuperar clave Virtual.',
        icon: 'fa-table-columns',
        targetId: 'tour-target-admin-tabs'
      },
      {
        title: 'Sigue la barra de progreso',
        text: 'En Recuperar clave Virtual, cada etapa aparece arriba: identificación, verificación, código, nueva clave y éxito.',
        tips: [
          'Completa un paso para avanzar al siguiente.',
          'Puedes volver atrás si necesitas corregir un dato (según el paso).'
        ],
        icon: 'fa-list-ol',
        targetId: 'tour-target-admin-progress'
      },
      {
        title: 'Completa el formulario actual',
        text: 'En el panel central ingresa la información que se te pide en cada etapa.',
        tips: [
          'Revisa bien el correo antes de solicitar el código.',
          'La nueva contraseña debe cumplir las reglas de longitud y seguridad indicadas.'
        ],
        icon: 'fa-pen-to-square',
        targetId: 'tour-target-admin-flow'
      },
      {
        title: '¡Listo!',
        text: 'Cuando termines, podrás usar la nueva clave para firmar con Certifirma Digital Virtual.',
        textSecondary: 'Si te atoras, vuelve a abrir esta guía con el botón “Guía”.',
        icon: 'fa-circle-check',
        targetId: null
      }
    ]
  },

  'admin-rse': {
    id: 'admin-rse',
    name: 'Administración · Certifirma Digital Virtual con OTP',
    steps: [
      {
        title: 'Gestionar Certifirma Digital Virtual con OTP',
        text: 'Este módulo es para Certifirma Virtual con código de seguridad (OTP) por correo.',
        tips: [
          'Puedes descargar el certificado o recuperar la clave si la olvidaste.',
          'En Recuperar clave: identifícate, valida el código y define la nueva clave.'
        ],
        icon: 'fa-shield-halved',
        targetId: null
      },
      {
        title: 'Entrar al módulo correcto',
        text: 'Administración → Certifirma Digital Virtual con OTP.',
        icon: 'fa-shield-halved',
        targetId: 'tour-target-admin-rse',
        ensureSidebarOpen: true,
        ensureAdminMenuOpen: true
      },
      {
        title: 'Elige la acción',
        text: 'Cambia entre Descargar certificado y Recuperar clave Virtual con las pestañas.',
        icon: 'fa-table-columns',
        targetId: 'tour-target-admin-tabs'
      },
      {
        title: 'Barra de etapas',
        text: 'En la pestaña Recuperar clave Virtual, la línea superior muestra el avance de la recuperación.',
        tips: [
          'No cierres la ventana mientras validas el código.',
          'Si el código vence, puedes solicitar uno nuevo desde el paso correspondiente.'
        ],
        icon: 'fa-list-ol',
        targetId: 'tour-target-admin-progress'
      },
      {
        title: 'Datos y nueva contraseña',
        text: 'Completa cada formulario del centro hasta confirmar el cambio.',
        tips: [
          'Guarda la nueva contraseña en un lugar seguro.',
          'Después podrás firmar usando Certifirma Digital Virtual con OTP.'
        ],
        icon: 'fa-lock',
        targetId: 'tour-target-admin-flow'
      },
      {
        title: '¡Listo!',
        text: 'El recorrido quedó claro. Usa “Guía” en el encabezado si quieres repasarlo.',
        icon: 'fa-circle-check',
        targetId: null
      }
    ]
  }
};

export function resolveTourIdFromUrl(url: string): TourId | null {
  if (
    url.includes('administrar/certifirma-virtual-otp')
    || url.includes('administrar/token-virtual-codigo')
    || url.includes('administrar-token-virtual-rse')
  ) {
    return 'admin-rse';
  }
  if (
    url.includes('administrar/certifirma-virtual')
    || url.includes('administrar/token-virtual')
    || url.includes('administrar-token-virtual')
  ) {
    return 'admin-tv';
  }
  if (url.includes('verificar')) return 'verify';
  if (url.includes('firmar')) return 'sign';
  return null;
}
